import { z } from "zod";
import type { AccreditationCategoryRef, AccreditationOrgRef, AccreditationRef, AccreditationUserRef } from "./accreditation-records";
import { PAGE_SIZE_MAX, paginationQuery } from "./pagination";

/**
 * Quotas (M4): how many cards an organisation may register per category
 * (main function), per games, with optional women / men bounds, plus rules
 * evaluated against the live counts (`REQUIRED`, `RATIO`). Over quota is
 * SHOWN, never blocked. Data, not constants: Settings → Quotas edits them.
 *
 * Live = every card that still stands (status not REJECTED / WITHDRAWN), the
 * same count the records summary uses.
 */

// ── Limits ──────────────────────────────────────────────────────────────────

/** Rows one `PUT /quotas` may carry (one organisation × its categories). */
export const QUOTA_ROWS_MAX = 200;
/** Upper bound of any one quota number; a guard against typos, not a policy. */
export const QUOTA_VALUE_MAX = 100_000;
export const QUOTAS_PAGE_SIZE_DEFAULT = 50;

// ── Quotas ──────────────────────────────────────────────────────────────────

/** `GET /quotas` — offset (a small admin lookup), ADMIN / SUPER_ADMIN. */
export const listQuotasQuery = paginationQuery.extend({
	games_id: z.string().uuid(),
	responsible_organization_id: z.string().uuid().optional(),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(QUOTAS_PAGE_SIZE_DEFAULT),
});
export type ListQuotasQuery = z.infer<typeof listQuotasQuery>;

export interface QuotaBounds {
	quota: number;
	women_min: number | null;
	women_max: number | null;
	men_min: number | null;
	men_max: number | null;
}

/** One row of `GET /quotas`. */
export interface QuotaListItem extends QuotaBounds {
	quota_id: string;
	games_id: string;
	responsible_organization: AccreditationOrgRef;
	main_function: AccreditationCategoryRef;
	updated_at: string;
	updated_by: AccreditationUserRef | null;
}

export interface QuotaFormMessages {
	quotaRequired: string;
	valueInvalid: string;
	womenRange: string;
	menRange: string;
	minimumsOverQuota: string;
	rowsMax: string;
	rowDuplicate: string;
}

export const enQuotaFormMessages: QuotaFormMessages = {
	quotaRequired: "Enter a quota",
	valueInvalid: `A whole number from 0 to ${QUOTA_VALUE_MAX}`,
	womenRange: "Women minimum cannot exceed women maximum",
	menRange: "Men minimum cannot exceed men maximum",
	minimumsOverQuota: "Women and men minimums together cannot exceed the quota",
	rowsMax: `At most ${QUOTA_ROWS_MAX} categories at once`,
	rowDuplicate: "This category is listed twice",
};

const quotaValue = (m: QuotaFormMessages) => z.number({ invalid_type_error: m.valueInvalid }).int(m.valueInvalid).min(0, m.valueInvalid).max(QUOTA_VALUE_MAX, m.valueInvalid);

/** One category of `PUT /quotas`. Same CHECKs as the table (0020): min ≤ max, minimums fit the quota. */
export function makeQuotaRow(m: QuotaFormMessages = enQuotaFormMessages) {
	return z
		.object({
			main_function_id: z.string().uuid(),
			quota: quotaValue(m),
			women_min: quotaValue(m).nullish(),
			women_max: quotaValue(m).nullish(),
			men_min: quotaValue(m).nullish(),
			men_max: quotaValue(m).nullish(),
		})
		.refine((r) => r.women_min == null || r.women_max == null || r.women_min <= r.women_max, { path: ["women_min"], message: m.womenRange })
		.refine((r) => r.men_min == null || r.men_max == null || r.men_min <= r.men_max, { path: ["men_min"], message: m.menRange })
		.refine((r) => (r.women_min ?? 0) + (r.men_min ?? 0) <= r.quota, { path: ["quota"], message: m.minimumsOverQuota });
}
export const quotaRow = makeQuotaRow();
export type QuotaRowBody = z.input<typeof quotaRow>;

/**
 * `PUT /quotas` (SUPER_ADMIN) — REPLACE the quotas of one organisation in one
 * games: categories not listed lose their quota row. One statement each for
 * the delete and the upsert, never a loop.
 */
export function makeSetQuotasRequest(m: QuotaFormMessages = enQuotaFormMessages) {
	return z.object({
		games_id: z.string().uuid(),
		responsible_organization_id: z.string().uuid(),
		rows: z
			.array(makeQuotaRow(m))
			.max(QUOTA_ROWS_MAX, m.rowsMax)
			.refine((rows) => new Set(rows.map((r) => r.main_function_id)).size === rows.length, { message: m.rowDuplicate }),
	});
}
export const setQuotasRequest = makeSetQuotasRequest();
export type SetQuotasBody = z.input<typeof setQuotasRequest>;

// ── Usage (the Records quota card) ──────────────────────────────────────────

/**
 * `GET /quotas/usage` — any GMS role; a scoped login sees only the quota
 * rows and cards in its scope. Women / men counts come back ONLY when one
 * organisation is asked for (never an aggregate over the whole games).
 */
export const quotaUsageQuery = z.object({
	games_id: z.string().uuid(),
	responsible_organization_id: z.string().uuid().optional(),
});
export type QuotaUsageQuery = z.infer<typeof quotaUsageQuery>;

export interface QuotaGenderCount {
	women: number;
	men: number;
}

export interface QuotaUsageSubFunction {
	sub_function: AccreditationRef | null;
	used: number;
}

/**
 * One category: live cards against its quota (summed over the organisations in view).
 * Like for like: once any organisation in view has a quota row for the category, `used`
 * counts only the cards of organisations WITH a row; the rest are `unquoted_used`.
 */
export interface QuotaUsageFunction {
	main_function: AccreditationCategoryRef;
	/** Cards counted against `quota`; every card when `quota` is null. */
	used: number;
	/** null = no organisation in view has a quota row for this category. */
	quota: number | null;
	/** Cards of organisations with no quota row for this category (0 when `quota` is null). */
	unquoted_used: number;
	/** Σ per organisation of max(used − quota, 0) — one under its quota never hides another over. */
	over: number;
	/** Only with one organisation, and only when that organisation has a row for this category. */
	bounds: QuotaBounds | null;
	/** Only when one organisation is asked for. */
	gender: QuotaGenderCount | null;
	sub_functions: QuotaUsageSubFunction[];
}

export interface QuotaUsageOrganization {
	responsible_organization: AccreditationOrgRef;
	used: number;
	quota: number | null;
}

export interface QuotaRuleViolation {
	quota_rule_id: string;
	kind: QuotaRuleKind;
	responsible_organization: AccreditationOrgRef;
	subject_main_function: AccreditationCategoryRef;
	subject_sub_function: AccreditationRef | null;
	/** What the rule asks for (REQUIRED: min_count; RATIO: ceil(per count / per_count)). */
	required: number;
	actual: number;
}

export interface QuotaUsage {
	games_id: string;
	responsible_organization_id: string | null;
	/** The same like-for-like rule as `QuotaUsageFunction`, over every organisation × category. */
	total: { used: number; quota: number | null; unquoted_used: number; over: number };
	by_function: QuotaUsageFunction[];
	by_organization: QuotaUsageOrganization[];
	/** Active rules not met. Only when one organisation is asked for. */
	violations: QuotaRuleViolation[];
}

// ── Rules ───────────────────────────────────────────────────────────────────

/**
 *   REQUIRED  at least `min_count` cards of the subject ("a Chef de Mission is required")
 *   RATIO     at least ceil(count(per) / per_count) of the subject ("1 medical per 15 athletes")
 */
export const QUOTA_RULE_KINDS = ["REQUIRED", "RATIO"] as const;
export type QuotaRuleKind = (typeof QUOTA_RULE_KINDS)[number];

export const QUOTA_RULE_COUNT_MAX = 10_000;

/** `GET /quota-rules` — offset, ADMIN / SUPER_ADMIN. */
export const listQuotaRulesQuery = paginationQuery.extend({
	games_id: z.string().uuid(),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(QUOTAS_PAGE_SIZE_DEFAULT),
});
export type ListQuotaRulesQuery = z.infer<typeof listQuotaRulesQuery>;

/** One rule. No free-text name: the UI writes the sentence from `kind` + the names (i18n templates). */
export interface QuotaRule {
	quota_rule_id: string;
	games_id: string;
	kind: QuotaRuleKind;
	/** null = every organisation. */
	organization_type: AccreditationRef | null;
	subject_main_function: AccreditationCategoryRef;
	subject_sub_function: AccreditationRef | null;
	min_count: number | null;
	per_main_function: AccreditationCategoryRef | null;
	per_sub_function: AccreditationRef | null;
	per_count: number | null;
	is_active: boolean;
	updated_at: string;
	updated_by: AccreditationUserRef | null;
}

export interface QuotaRuleFormMessages {
	countInvalid: string;
	minCountRequired: string;
	perRequired: string;
	subjectRequired: string;
}

export const enQuotaRuleFormMessages: QuotaRuleFormMessages = {
	countInvalid: `A whole number from 1 to ${QUOTA_RULE_COUNT_MAX}`,
	minCountRequired: "Enter how many are required",
	perRequired: "Pick what the ratio counts and how many per one",
	subjectRequired: "Pick a category",
};

const ruleCount = (m: QuotaRuleFormMessages) => z.number({ invalid_type_error: m.countInvalid }).int(m.countInvalid).min(1, m.countInvalid).max(QUOTA_RULE_COUNT_MAX, m.countInvalid);

/**
 * `POST /quota-rules` / `PATCH /quota-rules/:id` (SUPER_ADMIN) — the full rule.
 * Same per-kind CHECK as the table: REQUIRED ⇒ `min_count` and no `per_*`;
 * RATIO ⇒ `per_main_function_id` and `per_count`, no `min_count`.
 */
export function makeQuotaRuleRequest(m: QuotaRuleFormMessages = enQuotaRuleFormMessages) {
	return z
		.object({
			games_id: z.string().uuid(),
			kind: z.enum(QUOTA_RULE_KINDS),
			organization_type_id: z.string().uuid().nullish(),
			subject_main_function_id: z.string({ required_error: m.subjectRequired }).uuid(m.subjectRequired),
			subject_sub_function_id: z.string().uuid().nullish(),
			min_count: ruleCount(m).nullish(),
			per_main_function_id: z.string().uuid().nullish(),
			per_sub_function_id: z.string().uuid().nullish(),
			per_count: ruleCount(m).nullish(),
			is_active: z.boolean().default(true),
		})
		.refine((r) => r.kind !== "REQUIRED" || r.min_count != null, { path: ["min_count"], message: m.minCountRequired })
		.refine((r) => r.kind !== "REQUIRED" || (r.per_main_function_id == null && r.per_sub_function_id == null && r.per_count == null), {
			path: ["per_main_function_id"],
			message: m.perRequired,
		})
		.refine((r) => r.kind !== "RATIO" || (r.per_main_function_id != null && r.per_count != null), { path: ["per_count"], message: m.perRequired })
		.refine((r) => r.kind !== "RATIO" || r.min_count == null, { path: ["min_count"], message: m.minCountRequired });
}
export const quotaRuleRequest = makeQuotaRuleRequest();
export type QuotaRuleBody = z.input<typeof quotaRuleRequest>;
